// ZZYON LEX — botão "⚡ Analisar com IA" no painel de Análise.
// Fluxo:
//  1. modal pede upload do PDF da petição
//  2. converte para base64 no browser
//  3. chama edge function lex-analyze-process
//  4. mostra preview do JSON estruturado retornado
//  5. usuário pode aceitar e popular docs/ações sugeridos automaticamente

import { lex, criarDoc, criarAcao } from './lex-client.js';
import { openModal, closeModal } from './lex-modal.js';
import { escapeHtml, fmtBRL, fmtDate, riscoBadge } from './utils.js';

const SUPABASE_URL =
  import.meta.env?.VITE_LEX_SUPABASE_URL ||
  'https://ywhmlvojowtsfetiyebg.supabase.co';
const SUPABASE_KEY =
  import.meta.env?.VITE_LEX_SUPABASE_KEY ||
  'sb_publishable_9mtwx7N6vCHcKKG7uBtisg_aR5rO5VU';

const FN_URL = `${SUPABASE_URL}/functions/v1/lex-analyze-process`;

// ─── Extração de texto do PDF (pdf.js carregado on-demand do CDN) ──────
// Estratégia: mandamos TEXTO PURO para a edge function (não PDF base64).
// Edge function Supabase tem timeout 150s e payload grande causa hang
// misterioso. Texto puro de 60-80 KB processa em ~15s.
//
// NOTA: usamos pdfjs-dist v3.11.174 (UMD legacy) que expõe `window.pdfjsLib`
// como global. Versões 4.x são ESM-only (.mjs) e dynamic import de URL
// externa falha CORS em produção Vercel.
const PDFJS_VERSION = '3.11.174';
const PDFJS_BASE = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build`;

function loadScriptOnce(src) {
  return new Promise((resolve, reject) => {
    // Já tem essa script tag? reaproveita
    if ([...document.scripts].some((s) => s.src === src)) {
      resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.crossOrigin = 'anonymous';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Falha ao carregar ${src}`));
    document.head.appendChild(s);
  });
}

async function loadPdfJs() {
  if (window.pdfjsLib) return window.pdfjsLib;

  // UMD bundle expõe window.pdfjsLib como side-effect
  await loadScriptOnce(`${PDFJS_BASE}/pdf.min.js`);

  if (!window.pdfjsLib) {
    throw new Error('pdf.js carregou mas window.pdfjsLib não existe — versão incompatível?');
  }

  window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.js`;
  return window.pdfjsLib;
}

async function pdfToText(file) {
  const pdfjs = await loadPdfJs();
  const buffer = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buffer }).promise;
  const partes = [];
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const content = await page.getTextContent();
    const linha = content.items.map((it) => it.str).join(' ');
    partes.push(`--- Página ${i} ---\n${linha}`);
  }
  return partes.join('\n\n');
}

// ─── UI principal ───────────────────────────────────────────────────────
export function abrirAnalisarComIA(processo, userEmail, onDone) {
  const wrap = document.createElement('div');
  wrap.innerHTML = `
    <div class="lex-disclaimer">
      A análise por IA é <strong>preliminar</strong>. Toda saída precisa ser revisada
      por advogado antes de virar input financeiro (provisão DRE, autorização de acordo).
      Custo aproximado: <strong>US$ 0,30–0,50 por petição</strong>.
    </div>

    <div class="lex-dropzone" id="lex-ia-dz">
      <strong>Anexar PDF da petição inicial</strong>
      <small>Aceita até ~30 MB. Claude lê PDFs vetorizados nativamente.</small>
      <input type="file" id="lex-ia-input" accept=".pdf,application/pdf" hidden />
    </div>

    <div id="lex-ia-status" style="margin-top:14px;font-size:13px;color:rgba(255,255,255,0.7)"></div>
    <div id="lex-ia-preview" style="margin-top:18px;display:none"></div>
  `;

  const foot = document.createElement('div');
  foot.style.cssText = 'display:contents';
  foot.innerHTML = `
    <button type="button" class="lex-btn-secondary" data-act="cancel">Cancelar</button>
    <button type="button" class="lex-btn" id="lex-ia-aceitar"
            style="width:auto;padding:10px 18px;display:none">
      Aceitar e popular docs/ações
    </button>
  `;

  const dz = wrap.querySelector('#lex-ia-dz');
  const input = wrap.querySelector('#lex-ia-input');
  const status = wrap.querySelector('#lex-ia-status');
  const preview = wrap.querySelector('#lex-ia-preview');
  const aceitarBtn = foot.querySelector('#lex-ia-aceitar');

  let lastResult = null;

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) {
      status.innerHTML = '<span style="color:#FF8C8C">Apenas PDFs.</span>';
      return;
    }
    const sizeMb = file.size / 1024 / 1024;
    status.innerHTML = `Extraindo texto de <strong>${escapeHtml(file.name)}</strong> (${sizeMb.toFixed(1)} MB)… <span class="lex-spin"></span>`;

    try {
      const peticao_texto = await pdfToText(file);
      const tamanho = peticao_texto.length;
      if (tamanho < 200) {
        status.innerHTML = `<span style="color:#FF8C8C">PDF parece estar vazio ou ser uma imagem escaneada (${tamanho} caracteres extraídos). OCR não suportado nesta fase.</span>`;
        return;
      }
      status.innerHTML = `Texto extraído (${(tamanho/1024).toFixed(1)} KB). Enviando para Claude… <span class="lex-spin"></span>`;

      const t0 = Date.now();
      const resp = await fetch(FN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_KEY,
          'Authorization': `Bearer ${SUPABASE_KEY}`,
        },
        body: JSON.stringify({
          processo_id: processo.id,
          peticao_texto,
          pdf_filename: file.name,
          modelo_preferido: 'haiku', // sonnet só em casos complexos
          contexto: {
            numero_cnj: processo.numero_cnj,
            tribunal: processo.tribunal,
            vara: processo.vara,
            comarca: processo.comarca,
            classe: processo.classe_processual,
            assunto: processo.assunto,
            contraparte: processo.contraparte,
            valor_causa_cadastrado: processo.valor_causa,
            data_distribuicao: processo.data_distribuicao,
            polo_icaro: processo.polo,
            filial: processo.filial_codigo,
          },
          criado_por_email: userEmail,
        }),
      });

      const data = await resp.json();
      const dur = ((Date.now() - t0) / 1000).toFixed(1);

      if (!resp.ok || data.error) {
        status.innerHTML = `<span style="color:#FF8C8C">Erro: ${escapeHtml(data.error || resp.statusText)}</span>`;
        return;
      }

      lastResult = data;
      status.innerHTML = `<span style="color:#6FE0A8">✓ Análise concluída em ${dur}s</span> · ${data.tokens?.input || 0} tokens IN / ${data.tokens?.output || 0} OUT · modelo ${data.modelo}`;
      preview.style.display = 'block';
      preview.innerHTML = renderPreview(data.analise, data.sugestoes);
      aceitarBtn.style.display = '';
      aceitarBtn.textContent = `Aceitar e popular ${data.sugestoes?.documentos?.length || 0} docs + ${data.sugestoes?.acoes?.length || 0} ações`;

    } catch (err) {
      status.innerHTML = `<span style="color:#FF8C8C">Falha: ${escapeHtml(err.message || err)}</span>`;
    }
  };

  dz.addEventListener('click', () => input.click());
  input.addEventListener('change', (e) => handleFile(e.target.files?.[0]));
  ['dragenter', 'dragover'].forEach(ev =>
    dz.addEventListener(ev, (e) => { e.preventDefault(); dz.classList.add('drag'); }));
  ['dragleave', 'drop'].forEach(ev =>
    dz.addEventListener(ev, (e) => { e.preventDefault(); dz.classList.remove('drag'); }));
  dz.addEventListener('drop', (e) => {
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  });

  foot.querySelector('[data-act="cancel"]').addEventListener('click', closeModal);

  aceitarBtn.addEventListener('click', async () => {
    if (!lastResult) return;
    aceitarBtn.disabled = true;
    aceitarBtn.textContent = 'Populando…';
    try {
      await popularSugestoes(processo.id, lastResult.sugestoes);
      closeModal();
      onDone?.();
    } catch (err) {
      alert('Erro ao popular: ' + (err.message || err));
      aceitarBtn.disabled = false;
    }
  });

  openModal({
    title: '⚡ Analisar com IA — petição inicial',
    body: wrap, footer: foot, wide: true,
  });
}

// ─── Renderiza preview do que a IA devolveu ─────────────────────────────
function renderPreview(a, sug) {
  const r = riscoBadge(a.risco);
  return `
    <div class="lex-section" style="margin:0">
      <h2>Preview da análise IA <span class="${r.cls}">${r.txt}</span></h2>
      <div style="padding:18px 20px">
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:14px;margin-bottom:18px">
          <div><div class="lex-key">Condenação esperada</div><div class="lex-val">${fmtBRL(a.valor_condenacao_estimado)}</div></div>
          <div><div class="lex-key">Acordo aceitável</div><div class="lex-val">${fmtBRL(a.valor_acordo_aceitavel)}</div></div>
          <div><div class="lex-key">Provisão recomendada</div><div class="lex-val">${fmtBRL(a.valor_provisao_recomendado)}</div></div>
        </div>
        ${a.risco_justificativa ? `
          <div style="margin-bottom:14px">
            <div class="lex-key">Justificativa</div>
            <div class="lex-val" style="white-space:pre-wrap">${escapeHtml(a.risco_justificativa)}</div>
          </div>` : ''}
        ${a.fato_gerador_resumo ? `
          <div style="margin-bottom:14px">
            <div class="lex-key">Fato gerador</div>
            <div class="lex-val" style="white-space:pre-wrap">${escapeHtml(a.fato_gerador_resumo)}</div>
            ${a.fato_gerador_data || a.fato_gerador_local ? `
              <div style="font-size:12px;color:rgba(255,255,255,0.55);margin-top:4px">
                ${a.fato_gerador_data ? `Data: ${escapeHtml(a.fato_gerador_data)}` : ''}
                ${a.fato_gerador_local ? ` · Local: ${escapeHtml(a.fato_gerador_local)}` : ''}
              </div>` : ''}
          </div>` : ''}
        ${a.estrategia_defesa ? `
          <div style="margin-bottom:14px">
            <div class="lex-key">Estratégia de defesa</div>
            <div class="lex-val" style="white-space:pre-wrap">${escapeHtml(a.estrategia_defesa)}</div>
          </div>` : ''}
        ${a.teses_defensivas?.length ? `
          <div style="margin-bottom:14px">
            <div class="lex-key">Teses defensivas</div>
            <ul style="margin:6px 0 0 18px;font-size:12px;color:rgba(255,255,255,0.85)">
              ${a.teses_defensivas.map(t => `<li><strong>${escapeHtml(t.tese)}</strong> — ${escapeHtml(t.fundamento_legal || '')}</li>`).join('')}
            </ul>
          </div>` : ''}
        ${sug.documentos?.length ? `
          <div style="margin-bottom:14px">
            <div class="lex-key">Documentos sugeridos (${sug.documentos.length})</div>
            <ul style="margin:6px 0 0 18px;font-size:12px;color:rgba(255,255,255,0.85)">
              ${sug.documentos.slice(0, 8).map(d => `<li>${escapeHtml(d.titulo)} <span style="color:rgba(255,255,255,0.5)">· ${escapeHtml(d.categoria || '—')}</span></li>`).join('')}
              ${sug.documentos.length > 8 ? `<li style="color:rgba(255,255,255,0.5)">… e mais ${sug.documentos.length - 8}</li>` : ''}
            </ul>
          </div>` : ''}
        ${sug.acoes?.length ? `
          <div style="margin-bottom:14px">
            <div class="lex-key">Ações sugeridas (${sug.acoes.length})</div>
            <ul style="margin:6px 0 0 18px;font-size:12px;color:rgba(255,255,255,0.85)">
              ${sug.acoes.slice(0, 8).map(ac => `<li><strong>${escapeHtml(ac.titulo)}</strong> <span style="color:rgba(255,255,255,0.5)">· ${escapeHtml(ac.tipo || '')} · prio ${escapeHtml(ac.prioridade || '')}</span></li>`).join('')}
            </ul>
          </div>` : ''}
        <div class="lex-disclaimer">
          A análise foi gravada como <strong>versão preliminar</strong> em
          <code>themis.processo_analise</code>. Não foi validada por advogado ainda.
        </div>
      </div>
    </div>
  `;
}

// ─── Popula docs e ações sugeridos pela IA ──────────────────────────────
async function popularSugestoes(processo_id, sug) {
  const docs = sug?.documentos || [];
  const acoes = sug?.acoes || [];
  const hoje = new Date();

  // Pegar próxima ordem para não duplicar com docs/ações já existentes
  const { data: docsExist } = await lex.from('processo_docs').select('ordem')
    .eq('processo_id', processo_id).order('ordem', { ascending: false }).limit(1);
  const { data: acoesExist } = await lex.from('processo_acoes').select('ordem')
    .eq('processo_id', processo_id).order('ordem', { ascending: false }).limit(1);

  let ordemDoc = (docsExist?.[0]?.ordem || -1) + 1;
  for (const d of docs) {
    await criarDoc({
      processo_id,
      ordem: ordemDoc++,
      categoria: d.categoria || null,
      titulo: d.titulo,
      descricao: d.descricao || null,
      obrigatorio: d.obrigatorio !== false,
      status: 'pendente',
    });
  }

  let ordemAc = (acoesExist?.[0]?.ordem || -1) + 1;
  for (const ac of acoes) {
    const prazo = new Date(hoje);
    prazo.setDate(prazo.getDate() + Number(ac.prazo_dias || 14));
    await criarAcao({
      processo_id,
      ordem: ordemAc++,
      titulo: ac.titulo,
      descricao: ac.descricao || null,
      tipo: ac.tipo || 'outro',
      prioridade: ac.prioridade || 'media',
      prazo: prazo.toISOString().slice(0, 10),
      status: 'aberta',
    });
  }
}
